import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import {
  describeApiError,
  getLibraryMeta,
  getLibraryProducts,
  unlockLibrary,
} from '../services/api';
import type { PrivateLibrary as LibraryMeta, Product } from '../types';
import ProductCard from '../components/ProductCard';

interface StoredSession {
  token: string;
  expiresAt: number; // ms epoch
}

const sessionKey = (slug: string) => `private-library:${slug}`;

const loadSession = (slug: string): StoredSession | undefined => {
  try {
    const raw = sessionStorage.getItem(sessionKey(slug));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredSession;
    if (parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(sessionKey(slug));
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
};

const saveSession = (slug: string, token: string, expiresIn: number) => {
  const session: StoredSession = { token, expiresAt: Date.now() + expiresIn * 1000 };
  sessionStorage.setItem(sessionKey(slug), JSON.stringify(session));
};

const PrivateLibrary: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const [meta, setMeta] = useState<LibraryMeta | undefined>();
  const [metaError, setMetaError] = useState<string | undefined>();
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [token, setToken] = useState<string | undefined>();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unlockError, setUnlockError] = useState<string | undefined>();

  const [products, setProducts] = useState<Product[] | undefined>();
  const [productsError, setProductsError] = useState<string | undefined>();

  useEffect(() => {
    if (!slug) return;
    setLoadingMeta(true);
    getLibraryMeta(slug)
      .then(setMeta)
      .catch(e => {
        if (e?.response?.status === 404) setMetaError('Library not found.');
        else setMetaError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load');
      })
      .finally(() => setLoadingMeta(false));

    const cached = loadSession(slug);
    if (cached) setToken(cached.token);
  }, [slug]);

  useEffect(() => {
    if (!slug || !token) return;
    setProductsError(undefined);
    getLibraryProducts(slug, token)
      .then(setProducts)
      .catch(e => {
        if (e?.response?.status === 401) {
          setToken(undefined);
          sessionStorage.removeItem(sessionKey(slug));
          return;
        }
        setProductsError(describeApiError(e, 'Failed to load products'));
      });
  }, [slug, token]);

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    setSubmitting(true);
    setUnlockError(undefined);
    try {
      const { token, expires_in } = await unlockLibrary(slug, password);
      saveSession(slug, token, expires_in);
      setToken(token);
      setPassword('');
    } catch (e: any) {
      if (e?.response?.status === 401) setUnlockError('Incorrect password.');
      else setUnlockError(e?.response?.data?.detail ?? e?.message ?? 'Unlock failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMeta) {
    return <div className="p-8 text-center text-text-light">Loading…</div>;
  }

  // A collection that's been taken down, or never existed, is a normal
  // thing to land on from a link shared in a group chat months ago. Both
  // look the same from here on purpose -- see the API.
  if (metaError) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-2xl mb-3">This collection isn't open right now</h1>
        <p className="text-text-light mb-6">
          Group collections come and go with the season. If you're expecting
          this one, check with whoever shared the link — or tell us what you
          need and we'll make it.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/custom-request"
            className="bg-primary text-white rounded px-5 py-3 text-sm font-medium hover:bg-primary-dark"
          >
            Request a custom piece
          </Link>
          <Link
            to="/home"
            className="border border-primary text-primary rounded px-5 py-3 text-sm font-medium hover:bg-primary/5"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <form
          onSubmit={handleUnlock}
          className="w-full max-w-sm bg-white rounded shadow-sm p-6 space-y-4"
        >
          <div className="flex items-center gap-2 text-primary">
            <Lock size={18} />
            <h1 className="text-lg font-semibold">{meta?.name}</h1>
          </div>
          {meta?.description && (
            <p className="text-sm text-text-light">{meta.description}</p>
          )}
          <div>
            <label className="block text-xs text-text-light mb-1">Password</label>
            <input
              required
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              className="w-full border border-background-dark rounded px-3 py-2 text-sm"
            />
          </div>
          {unlockError && <div className="text-sm text-red-600">{unlockError}</div>}
          <button
            type="submit"
            disabled={submitting || !password}
            className="w-full bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {submitting ? 'Unlocking…' : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{meta?.name}</h1>
        {meta?.description && (
          <p className="text-sm text-text-light mt-1">{meta.description}</p>
        )}
      </header>

      {productsError && <div className="text-sm text-red-600 mb-4">{productsError}</div>}

      {!products && !productsError && <div className="text-text-light">Loading products…</div>}

      {/* A collection can be set up before anything is put in it, so this is
          a normal state rather than a failure. Send them somewhere useful
          instead of leaving them on an empty page. */}
      {products && products.length === 0 && (
        <div className="border border-dashed border-background-dark rounded p-10 text-center">
          <p className="text-lg">Nothing in this collection yet.</p>
          <p className="text-sm text-text-light mt-2 max-w-md mx-auto">
            Pieces for {meta?.name ?? 'this group'} are still being put
            together. Check back soon — or tell us what you have in mind and
            we'll make it.
          </p>
          <Link
            to="/custom-request"
            className="inline-block mt-5 bg-primary text-white rounded px-5 py-3 text-sm font-medium hover:bg-primary-dark"
          >
            Request a custom piece
          </Link>
        </div>
      )}

      {products && products.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map(p => (
            <ProductCard key={p.product_id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PrivateLibrary;
